import React, { useEffect, useState, useRef } from 'react';
import { Building2, Loader2, Pencil, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { mergeLastCrearAnuncioState } from '../../utils/crearAnuncioStorage';

interface CompanyDataProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface CompanyProfileData {
  nombre: string | null;
  logo_url: string | null;
  rubro: string | null;
}

export const CompanyData: React.FC<CompanyDataProps> = ({ profile, setError, setSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [company, setCompany] = useState<CompanyProfileData>({ nombre: '', logo_url: null, rubro: null });
  const [nameInput, setNameInput] = useState('');
  const [rubroInput, setRubroInput] = useState('Moda e Indumentaria');
  const [rubroOption, setRubroOption] = useState<string>('Moda e Indumentaria');
  const [customRubro, setCustomRubro] = useState<string>('');
  const [savingRubro, setSavingRubro] = useState(false);
  const [marketingStrategy, setMarketingStrategy] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isStrategyExpanded, setIsStrategyExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const rubros = [
    'Moda e Indumentaria',
    'Hogar y Decoración',
    'Belleza y Cuidado Personal',
    'Tecnología',
    'Salud y Bienestar',
    'Deportes y Fitness',
    'Automotriz',
    'Gastronomía',
    'Otro'
  ];

  useEffect(() => {
    const loadData = async () => {
      if (!profile?.id_company) {
        setLoading(false);
        return;
      }

      try {
        setError(null);
        // Fetch company profile (name and logo)
        const { data: companyData, error: companyError } = await supabase
          .from('company_profiles')
          .select('nombre, logo_url, rubro')
          .eq('id_company', profile.id_company)
          .single();

        if (companyError) throw companyError;

        setCompany({ nombre: companyData?.nombre ?? '', logo_url: companyData?.logo_url ?? null, rubro: companyData?.rubro ?? null });
        setNameInput(companyData?.nombre ?? '');
        const rubroVal = companyData?.rubro ?? 'Moda e Indumentaria';
        setRubroInput(rubroVal);
        if (rubros.includes(rubroVal)) {
          setRubroOption(rubroVal);
          setCustomRubro('');
        } else if (rubroVal && rubroVal.trim().length > 0) {
          setRubroOption('Otro');
          setCustomRubro(rubroVal);
        }

        // Fetch marketing strategy from company schema config row 0
        const { data: cfg, error: cfgError } = await supabase
          .from('config')
          .select('media_agent_analysis')
          .eq('id_company', profile.id_company)
          .maybeSingle();

        if (!cfgError) {
          setMarketingStrategy(cfg?.media_agent_analysis ?? null);
        } else if (cfgError.code !== 'PGRST116') {
          // Ignore no rows error; otherwise raise
          throw cfgError;
        }
      } catch (err) {
        console.error('Error loading company data:', err);
        setError('Error al cargar los datos de la empresa');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id_company]);

  // Derived flags for Rubro UI (component scope)
  const effectiveRubro = (rubroOption === 'Otro' ? customRubro.trim() : rubroOption);
  const currentRubro = (company.rubro ?? 'Moda e Indumentaria');
  const showSaveRubro = (rubroOption !== 'Otro' || customRubro.trim().length > 0)
    && ((effectiveRubro !== currentRubro) || savingRubro);

  const handleSaveRubro = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id_company) return;
    try {
      setSavingRubro(true);
      setError(null);
      setSuccess(null);

      const { error } = await supabase
        .from('company_profiles')
        .update({ rubro: rubroInput })
        .eq('id_company', profile.id_company);

      if (error) throw error;

      setCompany(prev => ({ ...prev, rubro: rubroInput }));
      mergeLastCrearAnuncioState({ rubro: rubroInput });
      setSuccess('Rubro actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating company rubro:', err);
      setError('No se pudo actualizar el rubro');
    } finally {
      setSavingRubro(false);
    }
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id_company) return;
    if (!nameInput.trim()) {
      setError('El nombre de la empresa no puede estar vacío');
      return;
    }
    try {
      setSavingName(true);
      setError(null);
      setSuccess(null);

      const { error } = await supabase
        .from('company_profiles')
        .update({ nombre: nameInput.trim() })
        .eq('id_company', profile.id_company);

      if (error) throw error;

      setCompany(prev => ({ ...prev, nombre: nameInput.trim() }));
      setSuccess('Nombre de la empresa actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
      setIsEditingName(false);
    } catch (err) {
      console.error('Error updating company name:', err);
      setError('No se pudo actualizar el nombre de la empresa');
    } finally {
      setSavingName(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile?.id_company) return;
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      setSuccess(null);

      const timestamp = new Date().getTime();
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('system')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('system')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('company_profiles')
        .update({ logo_url: publicUrl })
        .eq('id_company', profile.id_company);

      if (updateError) throw updateError;

      setCompany(prev => ({ ...prev, logo_url: publicUrl }));
      setSuccess('Logo actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('No se pudo actualizar el logo');
    } finally {
      setUploadingLogo(false);
      // Clear the file input value
      (e.target as HTMLInputElement).value = '';
    }
  };

  // Nota: si se requiere eliminar logo, se puede reintroducir una acción que ponga logo_url en null.

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!profile?.id_company) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <p className="text-gray-600">Primero vinculá tu empresa en la sección Empleados.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-gray-700" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Datos de la empresa</h2>
            <p className="text-sm text-gray-500">Nombre, logo y estrategia de marketing</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Logo + Name combined layout */}
        <div className="flex flex-col md:flex-row md:items-start md:gap-8">
          {/* Logo column */}
          <div className="flex flex-col items-center md:items-center md:w-1/3">
            <div className="w-36 h-36 md:w-40 md:h-40 rounded-full overflow-hidden border border-gray-200 bg-white">
              <img
                src={company.logo_url || 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg'}
                alt="Logo de la empresa"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg';
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
              className="mt-3 h-10 px-6 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {uploadingLogo ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Subiendo...</span>
              ) : (
                'Editar'
              )}
            </button>
            {/* Hidden input for logo upload */}
            <input
              ref={fileInputRef}
              type="file"
              id="company-logo-upload"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              disabled={uploadingLogo}
            />
          </div>

          {/* Name column */}
          <div className="mt-6 md:mt-0 md:flex-1">
            <h3 className="text-md font-medium text-gray-900 mb-3">Nombre de la empresa</h3>
            {!isEditingName ? (
              <div className="flex items-center gap-2">
                <div className="text-gray-900">{company.nombre || 'Sin nombre'}</div>
                <button
                  type="button"
                  onClick={() => {
                    setNameInput(company.nombre || '');
                    setIsEditingName(true);
                  }}
                  className="p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  aria-label="Editar nombre"
                  title="Editar nombre"
                >
                  <Pencil className="w-4 h-4 text-gray-700" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSaveName} className="flex flex-col sm:flex-row sm:items-center gap-3 max-w-lg">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Nombre de la empresa"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={savingName}
                  className="h-10 px-5 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center gap-2"
                >
                  {savingName ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Guardar
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingName(false);
                    setNameInput(company.nombre || '');
                  }}
                  className="h-10 px-5 rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancelar
                </button>
              </form>
            )}

            <div className="mt-6">
              <h3 className="text-md font-medium text-gray-900 mb-3">Rubro</h3>
              <form onSubmit={handleSaveRubro} className="flex flex-wrap items-center gap-3 max-w-lg">
                <select
                  value={rubroOption}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRubroOption(v);
                    if (v !== 'Otro') {
                      setCustomRubro('');
                      setRubroInput(v);
                    } else {
                      setRubroInput(customRubro);
                    }
                  }}
                  className="min-w-[220px] w-auto h-10 px-4 rounded-full bg-white border border-gray-200 shadow-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shrink-0"
                >
                  {rubros.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                {/* Custom input renders outside of the relative select container to avoid overlay issues */}
                {rubroOption === 'Otro' && (
                  <input
                    type="text"
                    value={customRubro}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomRubro(val);
                      setRubroInput(val);
                    }}
                    placeholder="Especificá tu rubro"
                    className="w-full h-10 px-4 rounded-full bg-white border border-gray-200 shadow-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    required
                  />
                )}
                {showSaveRubro && (
                  <button
                    type="submit"
                    disabled={savingRubro}
                    className="h-10 px-5 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center gap-2"
                  >
                    {savingRubro ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Guardar
                      </>
                    )}
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Marketing Strategy */}
        <div>
          <h3 className="text-md font-medium text-gray-900">Memoria de la IA</h3>
          <p className="text-sm text-gray-600 mb-3">Estos son los análisis de la marca realizados por la IA para dar un mejor resultado en la generación de contenido.</p>
          {marketingStrategy ? (
            <div className="relative p-4 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm">
              <div className="text-sm font-medium text-gray-900 mb-2">Estrategia de marketing</div>
              <div className={`${isStrategyExpanded ? 'pb-14' : 'line-clamp-4'} whitespace-pre-wrap`}> 
                {marketingStrategy}
              </div>
              {!isStrategyExpanded && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 via-gray-30 to-transparent rounded-b-md" />
              )}
              <button
                type="button"
                onClick={() => setIsStrategyExpanded(v => !v)}
                className="absolute bottom-2 right-2 z-10 h-8 px-4 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isStrategyExpanded ? 'Ver menos' : 'Ver más'}
              </button>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-md text-gray-500 text-sm">
              No hay información disponible todavía. Completá la descripción de marca para generar la estrategia.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
